import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AuthService } from '../core/auth.service';
import { LanguageService } from '../core/language';
import { formatPct, formatPrice } from '../core/format';
import { Quote } from '../core/models';
import { DeskService } from '../core/desk.service';
import { MarketService } from '../core/market.service';
import { Sparkline } from '../ui/sparkline';

type Lens = 'focus' | 'pace' | 'nerve';

@Component({
  selector: 'app-home',
  imports: [RouterLink, Sparkline],
  templateUrl: './home.html',
})
export class HomePage implements OnDestroy {
  protected readonly market = inject(MarketService);
  protected readonly auth = inject(AuthService);
  protected readonly lang = inject(LanguageService);
  protected readonly partnerMarks = Array.from({ length: 12 }, () => ['jupiter', 'tradingview', 'trust'] as const).flat();
  private readonly assistant = inject(DeskService);
  protected readonly price = formatPrice;
  protected readonly pct = formatPct;
  protected readonly lens = signal<Lens>('focus');
  protected readonly reviewIndex = signal(0);
  protected readonly reviewHold = signal(false);
  protected readonly reviewRun = signal(0);
  protected readonly reviews = computed(() => [
    { mark: 'A', name: 'Amira K.', topic: this.lang.t('homeR1t'), quote: this.lang.t('homeR1') },
    { mark: 'J', name: 'Jonas P.', topic: this.lang.t('homeR2t'), quote: this.lang.t('homeR2') },
    { mark: 'L', name: 'Leila M.', topic: this.lang.t('homeR3t'), quote: this.lang.t('homeR3') },
  ]);
  private reviewTimer = 0;

  constructor() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    this.armReviews();
  }

  ngOnDestroy(): void {
    window.clearInterval(this.reviewTimer);
  }

  protected card(symbol: string): Quote | undefined {
    return this.market.quotes().find((quote) => quote.symbol === symbol);
  }

  protected readonly desk = computed(() => {
    const wanted = ['NVDA', 'AAPL', 'BTC-USD', 'XAUUSD'];
    return wanted
      .map((symbol) => this.market.quotes().find((quote) => quote.symbol === symbol))
      .filter((quote): quote is Quote => quote !== undefined);
  });

  protected readonly preview = computed(() => this.market.quotes().slice(0, 6));

  protected readonly brief = computed(() => {
    const quotes = this.market.quotes();
    if (quotes.length === 0) {
      return null;
    }
    const ranked = [...quotes].sort((a, b) => b.changePct - a.changePct);
    return {
      leader: ranked[0],
      laggard: ranked[ranked.length - 1],
      rising: quotes.filter((quote) => quote.changePct >= 0).length,
      total: quotes.length,
    };
  });

  protected openDesk(): void {
    this.assistant.show();
  }

  protected showReview(index: number): void {
    const count = this.reviews().length;
    this.reviewIndex.set((index + count) % count);
    this.reviewRun.update((run) => run + 1);
    if (!this.reviewHold()) {
      this.armReviews();
    }
  }

  protected holdReviews(hold: boolean): void {
    this.reviewHold.set(hold);
    if (!hold) {
      this.reviewRun.update((run) => run + 1);
      this.armReviews();
    }
  }

  private armReviews(): void {
    window.clearInterval(this.reviewTimer);
    this.reviewTimer = window.setInterval(() => {
      if (!this.reviewHold()) {
        this.showReview(this.reviewIndex() + 1);
      }
    }, 5200);
  }

  protected nextRead(): void {
    const order: Lens[] = ['focus', 'pace', 'nerve'];
    const index = order.indexOf(this.lens());
    this.lens.set(order[(index + 1) % order.length]);
  }
}
